import { TFunction } from 'i18next';
import { getDateTimeDiffMinute } from 'utils/datetime';
import { sortOnDate, sortOnNumber } from 'utils/sorting';
import Activity from './activity';
import GlobalDataCache from './globalDataCache/globalDataCache';
import ResourceLink, { ResourceLinkVersion } from './resourceLink';
import Task, { TaskStateId, TaskWorkflowStatus } from './tasks/task';

export const ApprovalMaxUsers = 10;

export enum ApprovalState {
  NotApproved = 1, //special state for items that were never submitted for approval
  Concept = 2, //task is created but not yet available for the user for approval
  Pending = 3, //task is assigned to the user, but not yet approved
  Approved = 4, //task is approved by the user
  Rejected = 5, //task is rejected by the user
}

export const ApprovalStateColor: Record<ApprovalState, string> = {
  [ApprovalState.Concept]: '#b86aec',
  [ApprovalState.Pending]: '#e2e07f',
  [ApprovalState.Approved]: '#8ae77e',
  [ApprovalState.Rejected]: '#f05050',
  [ApprovalState.NotApproved]: '#83c1dd',
};

export class Approval {
  link: ResourceLink | undefined;

  mainTask: Task;

  approvalTasks: Task[];

  constructor() {
    this.mainTask = new Task();
    this.approvalTasks = [];
  }

  workflowActive(): boolean {
    return this.mainTask.webhookStatus === TaskWorkflowStatus.Success;
  }

  getMainStatus(cache: GlobalDataCache): ApprovalMainStatus {
    const mainStatus = new ApprovalMainStatus();
    mainStatus.taskId = this.mainTask.taskId;
    mainStatus.templateId = this.mainTask.templateId;

    //set deadline except for concept state
    if (this.mainTask.taskStateId !== TaskStateId.Rejected) {
      mainStatus.deadline = this.mainTask.getDeadline(cache);
    }

    if (!this.workflowActive()) {
      //no workflow has been started

      //check if this is a concept
      if (this.mainTask.taskStateId === TaskStateId.Rejected) {
        mainStatus.status = ApprovalState.Concept;
        mainStatus.date = this.mainTask.created;
      } else {
        //the main status is based on the sub-statusses: everyone must approve
        const details = this.getDetailStatus();
        let approvedCount: number = 0;
        let maxApprovedDate: Date = new Date(0);

        for (let idx = 0; idx < details.length; idx++) {
          const detail = details[idx];
          if (detail.status === ApprovalState.Rejected) {
            mainStatus.status = ApprovalState.Rejected;
            mainStatus.date = detail.date;

            return mainStatus;
          } else if (detail.status === ApprovalState.Approved) {
            approvedCount++;
            if (detail.date && getDateTimeDiffMinute(detail.date, maxApprovedDate) > 0) {
              maxApprovedDate = detail.date;
            }
          }
        }

        if (approvedCount === details.length) {
          mainStatus.status = ApprovalState.Approved;
          mainStatus.date = maxApprovedDate;
        } else {
          mainStatus.status = ApprovalState.Pending;
          mainStatus.date = this.mainTask.created;
        }
      }
    } else {
      //a workflow has been started
      //the workflow sets the final state on the main task
      mainStatus.status = this.mainTask.approved ?? ApprovalState.Pending;
      mainStatus.date = this.mainTask.completed ?? this.mainTask.created;
    }

    return mainStatus;
  }

  getUniqueUsers(): string[] {
    //all distinct users of all tasks
    let userIds: string[] = this.approvalTasks.filter((t) => t.userId).map((t) => t.userId as string);
    userIds = [...new Set(userIds)];

    return userIds;
  }

  getDetailStatus(): ApprovalDetailStatus[] {
    const details: ApprovalDetailStatus[] = [];

    for (let idx = 0; idx < this.approvalTasks.length; idx++) {
      const task = this.approvalTasks[idx];
      if (task.isAssigned()) {
        const status = new ApprovalDetailStatus();
        status.taskId = task.taskId;
        status.userId = task.userId ?? '';
        status.status =
          this.mainTask.taskStateId === TaskStateId.Rejected
            ? ApprovalState.Concept
            : task.approved ?? ApprovalState.Pending;
        status.date = task.completed ?? task.created;

        details.push(status);
      }
    }

    return details;
  }

  static getStatusText(status: ApprovalState | undefined, t: TFunction<string[]>): string {
    switch (status) {
      case ApprovalState.Approved:
        return t('library:ApprovalTasks.States.Approved');
      case ApprovalState.Rejected:
        return t('library:ApprovalTasks.States.Rejected');
      case ApprovalState.Pending:
        return t('library:ApprovalTasks.States.Pending');
      case ApprovalState.Concept:
        return t('library:ApprovalTasks.States.Concept');
      default:
        return t('library:ApprovalTasks.States.NotApproved');
    }
  }
}

export const getLatestApproval = (approvals: Approval[] | undefined): Approval | undefined => {
  if (!approvals || approvals.length === 0) {
    return undefined;
  }

  const sorted = [...approvals].sort((a, b) => {
    const ad = a.mainTask.created?.getTime() ?? 0;
    const bd = b.mainTask.created?.getTime() ?? 0;

    return bd - ad;
  });

  return sorted[0];
};

export const hasCurrentApprovedEvidence = (link: ResourceLink | undefined, cache: GlobalDataCache): boolean => {
  if (!link) {
    return false;
  }

  const latestApproval = getLatestApproval(link.approvals);
  if (!latestApproval) {
    return false;
  }

  const mainStatus = latestApproval.getMainStatus(cache);
  if (mainStatus.status !== ApprovalState.Approved) {
    return false;
  }

  if (mainStatus.date && link.versionInfo.date && getDateTimeDiffMinute(link.versionInfo.date, mainStatus.date) > 0) {
    return false;
  }

  return true;
};

export class ApprovalMainStatus {
  taskId: number;

  status: ApprovalState;

  date: Date | undefined;

  deadline: Date | undefined;

  templateId: number | undefined;

  constructor() {
    this.taskId = 0;
    this.status = ApprovalState.Pending;
  }
}

export class ApprovalDetailStatus {
  taskId: number;

  userId: string;

  status: ApprovalState;

  date: Date | undefined;

  constructor() {
    this.taskId = 0;
    this.userId = '';
    this.status = ApprovalState.Pending;
    this.date = new Date();
  }
}

export class ApprovalVersion {
  version: string;

  date: Date | undefined;

  activity: Activity | undefined;

  constructor() {
    this.version = '';
  }

  static sortOnApprovalVersion = (v1: ApprovalVersion, v2: ApprovalVersion): number => {
    if (!v1.date && !v2.date) return 0;
    if (!v1.date) return 1;
    if (!v2.date) return -1;

    const result = sortOnDate(v1.date, v2.date) * -1;

    //when the date is equal, the approval record must be sorted above the version record for merging
    if (result === 0) {
      if (!v1.version && v2.version) {
        return 1;
      } else if (v1.version && !v2.version) {
        return -1;
      }
    }

    return result;
  };

  static getApprovalVersions = (versions: ResourceLinkVersion[], activities: Activity[]): ApprovalVersion[] => {
    const approvalVersions: ApprovalVersion[] = [];

    versions.forEach((v) => {
      if (v.hasData()) {
        const newApprovalVersion = new ApprovalVersion();
        newApprovalVersion.version = v.version;
        newApprovalVersion.date = v.date;
        approvalVersions.push(newApprovalVersion);
      }
    });

    activities.forEach((a) => {
      const newApprovalVersion = new ApprovalVersion();
      newApprovalVersion.date = a.created;
      newApprovalVersion.activity = a;
      approvalVersions.push(newApprovalVersion);
    });

    approvalVersions.sort((a, b) => ApprovalVersion.sortOnApprovalVersion(a, b));
    const approvalVersionsToRemove: number[] = [];

    //merge approvals with versions
    for (let idx = 0; idx < approvalVersions.length; idx++) {
      const approvalVersion = approvalVersions[idx];
      let latestApprovalIdx = -1;
      if (!approvalVersion.version) {
        //when this is an approval (and not a version)
        //-remember this index
        //-skip to the next version
        latestApprovalIdx = idx++;
        while (idx < approvalVersions.length && !approvalVersions[idx].version) {
          approvalVersionsToRemove.push(idx++);
        }

        if (idx < approvalVersions.length) {
          approvalVersions[idx].activity = approvalVersions[latestApprovalIdx].activity;
          approvalVersionsToRemove.push(latestApprovalIdx);
        }
      }
    }

    //remove the approvals that were done 'in between': we only want to see the latest approval for a version in this report
    approvalVersionsToRemove.sort((a, b) => -1 * sortOnNumber(a, b));
    for (let idx = 0; idx < approvalVersionsToRemove.length; idx++) {
      approvalVersions.splice(approvalVersionsToRemove[idx], 1);
    }

    return approvalVersions;
  };
}
